// Requirements stored in `requirements_config` on the workspace in the
// backend (`helper/workspaceRequirementsConfig.js`). The older ones stay in
// `tenant_requirements`, `affidavit_activated` and `protected_housing_enabled`.
// `canRequire: false` means the item can only be on or off.
export const WORKSPACE_REQUIREMENTS = [
  {
    key: "kyc",
    label: "Identity verification (KYC)",
    description: "Valid ID, liveness, face match and tamper detection.",
    canRequire: false,
  },
  {
    key: "aml",
    label: "AML check",
    description:
      "Sanctions and politically exposed persons (PEP) screening. Needs Identity verification (KYC).",
    canRequire: false,
    // The AML screening only runs inside the KYC step
    needs: "kyc",
  },
  {
    key: "basic_documents",
    label: "Basic documents",
    description:
      "Skor requests and analyses ID, income and employment documents.",
    canRequire: false,
  },
  {
    key: "document_forensics",
    label: "Document forensics",
    description:
      "Detects tampered or generated documents. Needs Basic documents.",
    canRequire: false,
    needs: "basic_documents",
  },
  {
    key: "defaulter_check",
    label: "Defaulter check",
    description: "Checks debtor and rent-default registries.",
    canRequire: true,
  },
  {
    key: "financial_assessment",
    label: "Financial assessment",
    description: "Income verified with bank statements.",
    canRequire: true,
  },
  {
    key: "open_banking",
    label: "Open Banking",
    description: "The applicant connects their bank.",
    canRequire: true,
  },
  {
    key: "guarantor",
    label: "Guarantor",
    description: "Applicants can add guarantors, checked the same way.",
    canRequire: false,
  },
];

export const QUESTION_TYPES = [
  ["text", "Free text"],
  ["number", "Number"],
  ["yes_no", "Yes / No"],
  ["single_choice", "Single choice"],
  ["multiple_choice", "Multiple choice"],
  ["date", "Date"],
  ["link", "Link"],
  ["file", "File"],
];
// Types the admin can pick for a new question. "File" is no longer offered;
// it stays in QUESTION_TYPES so existing file questions keep their label.
export const NEW_QUESTION_TYPES = QUESTION_TYPES.filter(
  ([key]) => key !== "file",
);
export const CHOICE_TYPES = ["single_choice", "multiple_choice"];

// Protected housing (VPO) declarations, Skor standard list. Shown for
// information: the admin turns them on as a whole, not one by one (backend:
// `helper/workspaceRequirementsConfig.js`).
export const PROTECTED_HOUSING_DECLARATIONS = [
  "Declaración responsable de unidad de convivencia",
  "Declaración responsable de estado civil",
  "Declaración responsable de ingresos",
  "Declaración jurada de no propiedad",
  "Declaración responsable de ambos padres (menores sin DNI)",
  "Declaración de la renta equivalente del país de trabajo + declaración responsable",
];
// Index of the civil status declaration in the list above
export const CIVIL_STATUS_DECLARATION = 1;
// Shown under the civil status declaration for information: the tenant
// declares one of them. The admin does not pick them.
export const CIVIL_STATUSES = [
  "Soltero/a",
  "Casado/a",
  "Separado/a legalmente",
  "Divorciado/a",
  "Pareja de hecho inscrita",
  "Viudo/a",
];

// Protected housing (VPO) documents the admin can ask for, each on its own.
// Same keys as `PROTECTED_HOUSING_DOCUMENTS` in the backend
// (`helper/workspaceRequirementsConfig.js`).
export const PROTECTED_HOUSING_DOCUMENTS = [
  [
    "emancipation_proof",
    "Proof of emancipation (notarial deed or court authorization) (Emancipated minor)",
  ],
  [
    "registry_note",
    "Registry note (Nota Simple) for each ownership interest listed in the property-location search",
  ],
  ["inheritance_award_deed", "Inheritance award deed"],
  [
    "badalona_vpo_registry",
    "Enrollment in Badalona's subsidized-housing (VPO) applicant registry",
  ],
  [
    "vpo_registry",
    "Enrollment in the subsidized-housing (VPO) applicant registry (Badalona/Barcelona)",
  ],
];

// Same rule as the backend: off unless saved as on, and off while protected
// housing is off
export const normalizeProtectedHousingDeclarations = (
  value = {},
  enabled = true,
) => ({ active: enabled && value?.active === true });
// Always asked for while protected housing is on: the admin cannot untick it.
// Same as `ALWAYS_ON_PROTECTED_HOUSING_DOCUMENTS` in the backend.
export const ALWAYS_ON_PROTECTED_HOUSING_DOCUMENTS = ["emancipation_proof"];

// Each document: off unless saved as on (always-on ones are always on), and
// all off while protected housing is off
export const normalizeProtectedHousingDocuments = (
  value = {},
  enabled = true,
) =>
  Object.fromEntries(
    PROTECTED_HOUSING_DOCUMENTS.map(([key]) => [
      key,
      enabled &&
        (ALWAYS_ON_PROTECTED_HOUSING_DOCUMENTS.includes(key) ||
          value?.[key] === true),
    ]),
  );
export const MAX_QUESTIONS = 20;
export const MAX_OPTIONS = 10;

// Same rules as the backend: an item that is off, or cannot be required, is
// never required; an item that needs another one is off while that one is off.
// An item never saved on the workspace (for example `kyc` on a workspace saved
// before it existed) is on, as in the backend's getRequirementsConfig: only a
// saved `active: false` is off.
export const normalizeRequirementsConfig = (config = {}) => {
  const result = {};
  for (const { key, canRequire, needs } of WORKSPACE_REQUIREMENTS) {
    const active =
      config?.[key]?.active !== false && (!needs || result[needs].active);
    result[key] = {
      active,
      required_before_sharing:
        active && canRequire && config?.[key]?.required_before_sharing === true,
    };
  }
  result.applicant_questions = {
    linkedin: config?.applicant_questions?.linkedin === true,
    household_members: config?.applicant_questions?.household_members !== false,
    minors: config?.applicant_questions?.minors !== false,
    dogs: config?.applicant_questions?.dogs !== false,
    custom: config?.applicant_questions?.custom || [],
  };
  result.protected_housing_declarations = normalizeProtectedHousingDeclarations(
    config?.protected_housing_declarations,
  );
  result.protected_housing_documents = normalizeProtectedHousingDocuments(
    config?.protected_housing_documents,
  );
  return result;
};
